import React, { ReactNode, createContext, useContext } from 'react';
import Preloader from 'components/Preloader';
import { useSchemaFields } from 'hooks/useSchemaFields';
import { useSchema } from 'domain/ClientService';

type ClientsSchemaValue = {
    schemasColumns: ReturnType<typeof useSchemaFields>;
    schemasLists: {
        data: unknown;
        isLoading: boolean;
        error: unknown;
    };
};

const ClientsSchemaContext = createContext<ClientsSchemaValue | null>(null);

export const useClientsSchema = (): ClientsSchemaValue => {
    const context = useContext(ClientsSchemaContext);

    if (!context) {
        throw new Error('useClientsSchema can only be used inside ClientsSchemaContext');
    }

    return context;
};

const ClientsSchemaProvider = ({ children }: { children: ReactNode }) => {
    const {
        dataLists,
        isLoadingLists,
        errorLists,
    } = useSchema({ loadInitially: true });

    const schemasColumns = useSchemaFields('editCustomer');

    return (
        <ClientsSchemaContext.Provider value={{
            schemasColumns,
            schemasLists: {
                data: dataLists,
                isLoading: isLoadingLists,
                error: errorLists,
            },
        }}
        >
            <Preloader
                isLoading={schemasColumns.isLoading}
                error={schemasColumns.error}
            >
                {children}
            </Preloader>
        </ClientsSchemaContext.Provider>
    );
};

export const connectClientsSchema = <P extends object>(Component: React.ComponentType<P>) => (
    (props: P) => (
        <ClientsSchemaProvider>
            <Component {...props} />
        </ClientsSchemaProvider>
    )
);

export default ClientsSchemaProvider;
